import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import styles from './Grid.module.scss';
export type GridAs = 'div' | 'section' | 'article';
export type GridBreakpoint = 'base' | 'sm' | 'md' | 'lg' | 'xl';
export type GridCollapseBelow = 'sm' | 'md' | 'lg' | 'none';
export type GridColumns = 1 | 2 | 3 | 4 | 12;
/**
 * A ruled grid's column counts: 1-4 (its rules between the cells are
 * generated for those counts; a 12-column grid is for `Grid.Item` spans,
 * which a ruled grid's cells do not take).
 */
export type GridRuledColumns = Exclude<GridColumns, 12>;
export type GridGap = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type GridResponsiveColumns = GridColumns | Partial<Record<GridBreakpoint, GridColumns>>;
export type GridRuledResponsiveColumns = GridRuledColumns | Partial<Record<GridBreakpoint, GridRuledColumns>>;
export type GridItemAs = 'div' | 'li' | 'article' | 'section';
export type GridItemSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type GridResponsiveSpan = GridItemSpan | Partial<Record<GridBreakpoint, GridItemSpan>>;

/**
 * The grid's treatment. `default`: the cells sit `gap` apart and draw
 * nothing. `ruled` (Phase 6, the redesign's hairline grids on About): 1px
 * rules between the cells only, none round the block (no outer frame), in
 * the separator role (`border-separator`, the metric grid's rules: #949494,
 * #6b6b6b in the dark theme; the same on every surface), following the
 * `columns` prop at every breakpoint. A ruled grid takes 1-4 columns
 * (typed: `GridRuledColumns`, no 12), and every cell takes one track, is
 * inset by `space-6` (24px) and sized by its content. The rules are the
 * space between the cells, so a ruled grid takes no `gap`.
 */
export type GridVariant = 'default' | 'ruled';
interface GridBaseProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style'> {
  as?: GridAs;
  children: ReactNode;
  className?: never;
  collapseBelow?: GridCollapseBelow;
  columns?: GridResponsiveColumns;
  style?: never;
}

/** The default grid: its cells `gap` apart (`md` by default). */
export interface GridDefaultProps extends GridBaseProps {
  gap?: GridGap;
  variant?: 'default';
}

/**
 * The ruled grid: hairline rules instead of a gap, 1-4 columns at every
 * breakpoint (no 12: its rules are generated for 1-4 columns).
 */
export interface GridRuledProps extends GridBaseProps {
  columns?: GridRuledResponsiveColumns;
  gap?: never;
  variant: 'ruled';
}
export type GridProps = GridDefaultProps | GridRuledProps;
export interface GridItemProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style'> {
  as?: GridItemAs;
  children: ReactNode;
  className?: never;
  span?: GridResponsiveSpan;
  style?: never;
}
function responsiveClassNames<T extends number>(prefix: string, value: T | Partial<Record<GridBreakpoint, T>>) {
  if (typeof value === 'number') {
    return styles[`${prefix}-${value}`];
  }
  return (Object.entries(value) as Array<[GridBreakpoint, T | undefined]>).filter((entry): entry is [GridBreakpoint, T] => entry[1] !== undefined).map(([breakpoint, breakpointValue]) => styles[`${prefix}-${breakpoint}-${breakpointValue}`]);
}
function GridRoot(t0) {
  const $ = _c(19);
  let children;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    const {
      as: t6,
      children: t7,
      className: _className,
      collapseBelow: t8,
      columns: t9,
      gap: t10,
      style: _style,
      variant: t11,
      ...t12
    } = t0;
    t1 = t6;
    children = t7;
    t2 = t8;
    t3 = t9;
    t4 = t10;
    t5 = t11;
    props = t12;
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  const as = t1 === undefined ? "div" : t1;
  const collapseBelow = t2 === undefined ? "md" : t2;
  const columns = t3 === undefined ? 2 : t3;
  const gap = t4 === undefined ? "md" : t4;
  const variant = t5 === undefined ? "default" : t5;
  const Component = as as ElementType;
  const ruled = variant === "ruled";
  const t6 = ruled ? "ruled" : undefined;
  let t7;
  if ($[8] !== collapseBelow || $[9] !== columns || $[10] !== gap || $[11] !== ruled) {
    t7 = classNames(styles["helios-grid"], responsiveClassNames("helios-grid--columns", columns), typeof columns === "number" && collapseBelow !== "none" && styles[`helios-grid--collapse-${collapseBelow}`], ruled ? styles["helios-grid--ruled"] : styles[`helios-grid--gap-${gap}`]);
    $[8] = collapseBelow;
    $[9] = columns;
    $[10] = gap;
    $[11] = ruled;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== Component || $[14] !== children || $[15] !== props || $[16] !== t6 || $[17] !== t7) {
    t8 = <Component {...props} data-helios-grid={t6} className={t7}>{children}</Component>;
    $[13] = Component;
    $[14] = children;
    $[15] = props;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function GridItem(t0) {
  const $ = _c(14);
  let children;
  let props;
  let span;
  let t1;
  if ($[0] !== t0) {
    const {
      as: t2,
      children: t3,
      className: _className,
      span: t4,
      style: _style,
      ...t5
    } = t0;
    t1 = t2;
    children = t3;
    span = t4;
    props = t5;
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = span;
    $[4] = t1;
  } else {
    children = $[1];
    props = $[2];
    span = $[3];
    t1 = $[4];
  }
  const as = t1 === undefined ? "div" : t1;
  const Component = as as ElementType;
  let t2;
  if ($[5] !== span) {
    t2 = span && responsiveClassNames("helios-grid__item--span", span);
    $[5] = span;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = classNames(styles["helios-grid__item"], t2);
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== Component || $[10] !== children || $[11] !== props || $[12] !== t3) {
    t4 = <Component {...props} className={t3}>{children}</Component>;
    $[9] = Component;
    $[10] = children;
    $[11] = props;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
const Grid = Object.assign(GridRoot, {
  Item: GridItem
});
export default Grid;